import React from 'react';

import { IProductTemplateAttribute } from 'components/dashboard/product-template-table/utils/interfaces';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { DataTable } from 'primereact/datatable';
import { Tooltip } from 'react-tooltip';
import { T } from "services/lang/LanguageService";
import { CellType } from 'utils/enums';
import ProductTemplateAttributeDialog from './ProductTemplateAttributeDialog';

type ProductTemplateAttributeListProps = {
	attributes: IProductTemplateAttribute[];
	onAttributesChange: (newAttributes: IProductTemplateAttribute[]) => void;
};

const defaultAttribute: IProductTemplateAttribute = {
	displayName: 'Display name',
	type: CellType.STRING,
	referenceName: 'reference_name'
} 

const ProductTemplateAttributeList: React.FC<
	ProductTemplateAttributeListProps
> = ({ attributes, onAttributesChange }) => {
	const attributesCopy = [...attributes];
	
	const confirmOnDelete = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		index: number,
	) => {
		confirmPopup({
			target: e.currentTarget,
			message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-column"),
			icon: 'pi pi-exclamation-triangle',
			acceptClassName: 'p-button-danger',
			accept: () => deleteColumn(index),
		});
	};

	const deleteColumn = (index: number) => {
		attributesCopy.splice(index, 1);
		onAttributesChange(attributesCopy);
	};

	const updateAttributes = (
		newAttribute: IProductTemplateAttribute,
		index: number,
	) => {
		attributesCopy.splice(index, 1, newAttribute);
		onAttributesChange(attributesCopy);
	};

  const renderEdit = (rowData: IProductTemplateAttribute, options: ColumnBodyOptions) => (
    <ProductTemplateAttributeDialog
      index={options.rowIndex}
      attribute={rowData}
      onAttributeChange={updateAttributes}
      attributes={attributes}
    />
  );

  const renderDelete = (_: IProductTemplateAttribute, options: ColumnBodyOptions) => (
    <Button
      icon="pi pi-trash"
      className="p-button-sm p-button-danger"
      onClick={e => confirmOnDelete(e, options.rowIndex)}
      disabled={attributes.length === 1}
    />
  )

	return (
    <>
      <ConfirmPopup />
      <Accordion className="p-field p-col-12">
        <AccordionTab header={T("word.attributes")}>
          <DataTable
            value={attributes}
            size="small"
            editMode="row"
            reorderableRows
            onRowReorder={e => onAttributesChange(e.value)}
          >
            <Column field="displayName" header={T("word.column-name")} />
            <Column field="referenceName" header={T("word.reference-name")} />
            <Column field="type" header={T("word.type")} />
            <Column style={{ width: "0" }} body={renderEdit} />
            <Column style={{ width: "0" }} body={renderDelete} />
            <Column rowReorder style={{ width: "3em" }} />
          </DataTable>

          <div className="flex mt-3">
            <Tooltip id="add-attribute-btn" />
            <Button
              data-testid="add-attribute-btn"
              data-tooltip-id="add-attribute-btn"
              data-tooltip-content={T("action.add-attribute")}
              data-tooltip-place="right"
              icon="pi pi-plus"
              className="p-button-outlined p-button-rounded p-button-success mt-3 ml-3"
              onClick={() => onAttributesChange([...attributes, defaultAttribute])}
            />
          </div>
        </AccordionTab>
      </Accordion>
    </>
  );
};

export default ProductTemplateAttributeList;
